import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { useLanguage, localizedPath } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";

const PROJECT_TYPES = ["Einzelauftrag", "Monatliche Begleitung", "Editorial & Reise", "Etwas anderes"];
const BUDGETS = ["unter 1.000 €", "1.000 bis 5.000 €", "5.000 bis 10.000 €", "über 10.000 €"];

const inputClass =
  "w-full border-b border-black/20 bg-transparent py-3 text-bone placeholder:text-smoke/40 transition-colors duration-300 focus:border-ember focus:outline-none";

const initialForm = { name: "", email: "", project_type: "", budget: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const { t, lang } = useLanguage();
  useSeo({
    title: "Kontakt · Wim Jansen Delgado · Fotograf Hamburg",
    description:
      "Anfrage für Food-, Restaurant- und Hotelfotografie: Schreib Wim Jansen Delgado aus Hamburg. Antwort in der Regel innerhalb von 48 Stunden.",
    path: "/kontakt",
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", { ...form, budget: form.budget || null });
      toast.success(t("Vielen Dank! Ich melde mich persönlich bei dir. Versprochen."));
      setForm(initialForm);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-28 md:px-12 md:pt-36 lg:px-16">
        <Reveal>
          <p className="mb-4 font-mono text-sm uppercase tracking-[0.15em] text-smoke">{t("Kontakt")}</p>
        </Reveal>
        <h1
          data-testid="contact-title"
          className="font-serif font-light leading-[0.95] tracking-tight text-bone"
        >
          <MaskedLine delay={0.1} className="text-[clamp(3rem,9vw,7rem)]">
            {t("Erzähl mir von")}
          </MaskedLine>
          <MaskedLine delay={0.25} className="text-[clamp(3rem,9vw,7rem)] italic text-ember">
            {t("deinem Projekt.")}
          </MaskedLine>
        </h1>
        <Reveal delay={0.2} className="mt-10">
          <p className="max-w-xl text-[17px] font-light leading-[1.7] text-bone md:text-lg">
            {t("Ob Restaurant, Hotel, Reisereportage oder eine ganz eigene Idee: Schreib mir einfach. Ich lese jede Nachricht persönlich und antworte in der Regel innerhalb von 48 Stunden. Hilfreich sind ein paar Eckdaten: Ort, Zeitraum und wofür du die Bilder brauchst.")}
          </p>
        </Reveal>
      </section>

      <section className="border-t border-black/10 bg-coal" data-testid="contact-form-section">
        <div className="mx-auto max-w-7xl px-6 py-12 md:px-12 md:py-16 lg:px-16">
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="max-w-4xl" data-testid="contact-form">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
                  {t("Name *")}
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder={t("Dein Name")}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
                  {t("E-Mail *")}
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder={t("name@beispiel.de")}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-project-type" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
                  {t("Projektart *")}
                </label>
                <select
                  id="contact-project-type"
                  data-testid="contact-project-type-select"
                  required
                  value={form.project_type}
                  onChange={set("project_type")}
                  className={`${inputClass} cursor-pointer bg-obsidian`}
                >
                  <option value="" disabled>
                    {t("Bitte wählen")}
                  </option>
                  {PROJECT_TYPES.map((pt) => (
                    <option key={pt} value={pt}>{t(pt)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="contact-budget" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
                  {t("Budget (optional)")}
                </label>
                <select
                  id="contact-budget"
                  data-testid="contact-budget-select"
                  value={form.budget}
                  onChange={set("budget")}
                  className={`${inputClass} cursor-pointer bg-obsidian`}
                >
                  <option value="">{t("Bitte wählen")}</option>
                  {BUDGETS.map((b) => (
                    <option key={b} value={b}>{t(b)}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="contact-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
                  {t("Nachricht *")}
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-textarea"
                  required
                  minLength={5}
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder={t("Erzähl mir von deinem Vorhaben: Anlass, Zeitraum, Ort …")}
                  className={`${inputClass} resize-none`}
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={sending}
              className="mt-10 inline-flex items-center gap-3 border border-ember-deep bg-ember-deep px-10 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-ember-deep disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? t("Wird gesendet …") : t("Anfrage senden")} <ArrowRight size={14} />
            </button>
            <p className="mt-5 max-w-md text-xs font-light leading-relaxed text-smoke" data-testid="contact-privacy-hint">
              {t("Mit dem Absenden werden deine Angaben verarbeitet, um deine Anfrage zu beantworten. Mehr dazu in der")}{" "}
              <Link
                to={localizedPath("/datenschutz", lang)}
                data-testid="contact-privacy-link"
                className="underline decoration-ember-deep/50 underline-offset-4 transition-colors duration-300 hover:text-ember-deep"
              >
                {t("Datenschutzerklärung")}
              </Link>
              .
            </p>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
